import React, { useState, useEffect } from "react";
// ANT DESIGN COMPONENTS
import { Spin } from "antd";
// UUI ID
import { v4 as uuidv4 } from "uuid";
// REDUX
import { useSelector } from "react-redux";
import { usePostAllBanksPerformanceMutation } from "../../../redux/reports/reportsAPI";
// COMPONENTS
import ReportsTable from "../ReportsTable";
import ReportsHeader from "../ReportsHeader";
// AUTHORIZATION
import { useScreenPermission } from "../../../customHooks/useScopedPermission";
import UnauthorizedPage from "../../unauthorized/UnauthorizedPage";
// INTERFACES
import { RootState } from "../../../app/store";

const ReportGeneralPerformance = () => {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */

  // ************************************************ */
  // USE STATE VARIABLES **************************** */
  const [loadPage, setLoadPage] = useState(false);
  const [customData, setCustomData] = useState<any>([]);
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState("");
  const [sortDirection, setSortDirection] = useState("asc");

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const { theme } = useSelector((state: RootState) => state.home);
  const { filters } = useSelector((state: RootState) => state.report);
  // Reads SCREEN_PERMISSION["general-report-performance"] -- the same
  // declaration Layout.tsx's sidebar link reads.
  const allowed = useScreenPermission("general-report-performance");

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */
  const [triggerGetReport, { data, isLoading, isError, error }] =
    usePostAllBanksPerformanceMutation();

  // ************************************************ */
  // FUNCTIONS ************************************** */
  const generateTableRequestBody = ({ downloadReport }: any) => {
    const body: any = {
      server_id: filters.serverId,
      start_date: filters.startDate,
      end_date: filters.endDate,
      timezone: filters.timezoneCode,
      exclusions: {
        saturdays: filters.exclusionSaturdays ? "true" : "false",
        sundays: filters.exclusionSundays ? "true" : "false",
        holidays: filters.exclusionHolidays ? "true" : "false",
        time_ranges: filters.exclusionTimeList.join(","),
        date_ranges: filters.exclusionDateList.join(","),
      },
      download: downloadReport,
      page: currentPage,
      items_per_page: pageSize,
      sort_by: sortBy,
      sort_direction: sortDirection,
    };
    // Include lift type if value is not ALL
    if (filters.liftType !== "ALL") {
      body.lift_type = filters.liftType;
    }
    return body;
  };

  // ************************************************* */
  // USE EFFECT ************************************** */
  useEffect(() => {
    setLoadPage(true);
    setTimeout(() => {
      setLoadPage(false);
    }, 1000);
  }, []);

  useEffect(() => {
    if (data && data.status === "success") {
      const newData: any = { results: [], status: data.status, total: data.total };
      data.results.map((item: any) => {
        newData.results.push({ key: uuidv4(), ...item });
        return true;
      });
      setCustomData(newData);
    }
  }, [data]);

  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <Spin spinning={loadPage}>
      {allowed ? (
        <div className={`layout__contentPage__${theme}`}>
          <ReportsHeader
            reportDefaultData={{
              reportType: "REPORT",
              levelTypeName: "SERVERS",
              displaySelectTypeLevel: false,
              liftType: "ALL",
              defaultColumns: [
                "bank_name",
                "lift_name",
                "lift_type",
                "start_date",
                "end_date",
                "availability",
                "unavailability",
                "total_interruptions",
              ],
              defaultOffsets: [],
              infoDescriptionText: "report.infoDescriptionText3",
              displayDownloadCSVReport: true,
              displayDownloadPDFReport: true,
              reportTitle: "General / Performance",
              displayAdvanceFiltersExclusions: true,
              displayAdvanceFiltersOffsets: false,
              disabledOffsets: true,
              useTablePagination: true,
              displayDateByRange: false,
              displayDate: true,
              displaySelectLiftType: true,
              displayAdvanceFilterCharts: false,
              displayAdvanceFiltersTable: false,
              displayRecentWeeksPicket: false,
              displayInputLimitItems: false,
              displayChartMirror: false,
              chartMirror: false,
              chartType: "bar_stack_vertical",
              dateTypeOptions: [
                { id: "MONTH", name: "Month" },
                { id: "YEAR", name: "Year" },
              ],
              dateDaysLimit: 0,
              displayMinimumActivation: false,
              dateWeeksLimit: 0,
              dateMonthsLimit: 6,
              dateQuarterLimit: 0,
              dateYearsLimit: 0,
              dateType: "MONTH",
              bankSearchAndFilterString: "",
            }}
          />
          <ReportsTable
            response={customData}
            generateTableRequestBody={generateTableRequestBody}
            csvUrl={`${process.env.REACT_APP_LIFT_NET_CLOUD_API_ONLINE}/reports/all-banks/performance`}
            displayExpandibleRowTable
            groupColumns={null}
            triggerGetReport={triggerGetReport}
            isLoading={isLoading}
            isError={isError}
            error={error}
            updatePageSize={setPageSize}
            updateCurrentPage={setCurrentPage}
            sortBy={setSortBy}
            sortDirection={setSortDirection}
          />
        </div>
      ) : (
        <UnauthorizedPage />
      )}
    </Spin>
  );
};

export default ReportGeneralPerformance;
